import React, { useCallback, useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import authApiService from "../../services/authServices";
import Modal from "../../utils/modalUtils";

async function finalizeRegistration(registrationId, paymentIntentId, onSuccess) {
  const confirmRes = await authApiService.post("stripe/confirm-payment", {
    registrationId,
    paymentIntentId,
  });

  if (confirmRes?.code === 200) {
    onSuccess();
    return;
  }
  throw new Error(confirmRes?.message || "Registration failed");
}

function CheckoutForm({
  registrationId,
  paymentIntentId,
  amount,
  onSuccess,
  isProcessing,
  setIsProcessing,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const handlePay = useCallback(
    async (e) => {
      e.preventDefault();
      if (!stripe || !elements) return;

      setIsProcessing(true);
      try {
        const { error, paymentIntent } = await stripe.confirmPayment({
          elements,
          redirect: "if_required",
        });

        if (error) {
          Modal.fire({
            icon: "error",
            title: "Payment failed",
            text: error.message || "Could not complete payment.",
            confirmButtonText: "OK",
            confirmButtonColor: "#addd1e",
          });
          return;
        }

        const intentId = paymentIntent?.id || paymentIntentId;
        if (!intentId) {
          Modal.fire({
            icon: "error",
            title: "Payment failed",
            text: "Payment status could not be verified.",
            confirmButtonText: "OK",
            confirmButtonColor: "#addd1e",
          });
          return;
        }

        await finalizeRegistration(registrationId, intentId, onSuccess);
      } catch (err) {
        console.error(err);
        Modal.fire({
          icon: "error",
          title: "Registration Failed",
          text:
            err.response?.data?.message ||
            err.message ||
            "Failed to register. Please try again.",
          confirmButtonText: "OK",
          confirmButtonColor: "#addd1e",
        });
      } finally {
        setIsProcessing(false);
      }
    },
    [
      stripe,
      elements,
      paymentIntentId,
      registrationId,
      onSuccess,
      setIsProcessing,
    ]
  );

  return (
    <form onSubmit={handlePay} className="checkout-pay-form">
      {/* Accordion = radio-style list of payment types (Card, Link, wallets…) */}
      <PaymentElement
        options={{
          layout: {
            type: "accordion",
            defaultCollapsed: false,
            radios: true,
            spacedAccordionItems: true,
          },
          paymentMethodOrder: ["card", "link", "apple_pay", "google_pay"],
          wallets: {
            applePay: "auto",
            googlePay: "auto",
          },
          fields: {
            billingDetails: {
              address: {
                country: "auto",
              },
            },
          },
        }}
      />
      <button
        type="submit"
        className="checkout-pay-btn"
        disabled={!stripe || isProcessing}
      >
        {isProcessing
          ? "Processing…"
          : `Pay $${Number(amount || 0).toFixed(2)}`}
      </button>
      <p className="checkout-secure-note">
        Payments are processed securely by Stripe. We never store your card
        number.
      </p>
    </form>
  );
}

/**
 * Multi-method Stripe checkout (accordion: Card / Link / wallets when available).
 */
export default function StripePaymentSection({
  registrationId,
  onSuccess,
  isProcessing,
  setIsProcessing,
}) {
  const [publishableKey, setPublishableKey] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [amount, setAmount] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const configRes = await authApiService.get("stripe/config");
        const pk = configRes?.data?.publishableKey;
        if (!pk || configRes?.code >= 400) {
          if (!cancelled) {
            setLoadError(
              configRes?.message ||
                "Stripe is not configured on the server."
            );
          }
          return;
        }
        // Guard: never pass a secret key to Stripe.js (causes Invalid API Key: Sk_test_…)
        if (!String(pk).startsWith("pk_")) {
          if (!cancelled) {
            setLoadError(
              "Invalid Stripe publishable key on server (must start with pk_test_ or pk_live_). Secret key (sk_) cannot be used in the browser."
            );
          }
          return;
        }
        if (!cancelled) {
          setPublishableKey(pk);
          setStripePromise(loadStripe(pk));
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(
            e.response?.data?.message ||
              "Could not load payment configuration."
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!registrationId || !publishableKey) {
      setClientSecret(null);
      setPaymentIntentId(null);
      setAmount(null);
      return;
    }

    let cancelled = false;
    setClientSecret(null);
    setPaymentIntentId(null);
    setAmount(null);
    setLoadError(null);

    (async () => {
      try {
        const res = await authApiService.post("stripe/create-payment-intent", {
          registrationId,
        });
        if (cancelled) return;
        if (res?.code === 200 && res?.data?.clientSecret) {
          setClientSecret(res.data.clientSecret);
          setPaymentIntentId(res.data.paymentIntentId || null);
          setAmount(res.data.amount ?? null);
        } else {
          setLoadError(res?.message || "Could not start payment.");
        }
      } catch (e) {
        if (!cancelled) {
          setLoadError(e.response?.data?.message || "Could not start payment.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [registrationId, publishableKey]);

  if (loadError) {
    return <p className="checkout-error">{loadError}</p>;
  }

  if (!clientSecret || !stripePromise) {
    return <p className="checkout-loading">Preparing payment options…</p>;
  }

  return (
    <div className="checkout-card-panel">
      <h3>Select payment method</h3>
      <p className="checkout-hint" style={{ marginBottom: 14 }}>
        Choose Card, Link, or a digital wallet (when available on your device).
      </p>
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance: {
            theme: "stripe",
            variables: {
              colorPrimary: "#7a9234",
              borderRadius: "8px",
            },
          },
        }}
      >
        <CheckoutForm
          registrationId={registrationId}
          paymentIntentId={paymentIntentId}
          amount={amount}
          onSuccess={onSuccess}
          isProcessing={isProcessing}
          setIsProcessing={setIsProcessing}
        />
      </Elements>
    </div>
  );
}
